import { useEffect } from 'react';
import {
  useParams,
  useLocation,
  useNavigate,
  generatePath,
  useMatch,
  useOutletContext,
} from 'react-router-dom';
import { SendMessage } from 'react-use-websocket';
import {
  BlacklistDataContent,
  ButtonPseudo,
  IncomingSidepanel,
  SidePanel,
  SidePanelType,
  BlacklistUserInfo,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import useProblemIncomingTransaction from './hooks/use-incoming';
import {
  clearTransactionState,
  setPanelIsOpen,
} from '../../store/slices/problem-transactions/incoming-transaction';

const ProblemIncomingTransactionHOC = ({
  matchPath,
  backPath,
  generalPath,
  amlPath,
  orderPath,
  logPath,
  warningPath,
  chatPath,
  documentPath,
  changeLogPath,
  commentsPath,
}: {
  matchPath: string;
  backPath: string;
  generalPath: string;
  amlPath: string;
  orderPath: string;
  logPath: string;
  warningPath: string;
  chatPath: string;
  documentPath: string;
  changeLogPath: string;
  commentsPath: string;
}): React.JSX.Element => {
  const {
    documents,
    document: transactionDocument,
    transaction,
    panelIsOpen,
    warnings,
    blacklistDataVisible,
    blacklistData,
    blacklistItemVisibile,
    blacklistItem,
  } = useAppSelector((state) => state.customersProblemTransactions.incoming);
  const { currencies: currenciesList, activePermissions } = useAppSelector(
    (state) => state.root,
  );

  const dispatch = useAppDispatch();

  const { sendMessage } = useOutletContext<{
    sendMessage: SendMessage | null;
  }>();

  const { customerId, transactionId } = useParams();
  const { search } = useLocation();
  const navigate = useNavigate();
  const match = useMatch(matchPath);

  const {
    handleClose,
    transactionIsLoading,
    isLoadingAml,
    isLoadingLog,
    warningsIsLoading,
    isFetching,
    downloadInProgress,
    downloadIsFetching,
    imgLink,
    onDeleteDocument,
    deleteDocumentInProgress,
    deleteDocumentIsSuccess,
    onMoveDocument,
    documentCategoryChangeInProgress,
    onDownloadDocument,
    onSendDocumentComment,
    onEditComment,
    editCommentInProgress,
    editCommentIsSuccess,
    onDeleteComment,
    deleteCommentInProgress,
    commentInProgress,
    documentStatusChangeInProgress,
    onChangeCustomerDocumentsStatus,
    downloadFileIsSuccess,
    getTransactionDocumentyIdAction,
    documentIsLoading,
    documentIsFetching,
    handleCloseBlacklistData,
    getIncomingTransactionById,
  } = useProblemIncomingTransaction({
    transactionId,
    activePermissions,
    documents,
    document: transactionDocument,
    backPath,
    customerId,
    transaction,
  });

  useEffect(() => {
    if (match && customerId && transactionId) {
      navigate(
        generatePath(`${generalPath}${search || ''}`, {
          customerId,
          transactionId,
        }),
      );
    }
  }, [match, customerId, transactionId]);

  useEffect(() => {
    dispatch(setPanelIsOpen(true));

    return () => {
      dispatch(clearTransactionState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (customerId && !transaction && transactionId) {
      getIncomingTransactionById();
    }
  }, [getIncomingTransactionById, customerId, transaction, transactionId]);

  return (
    <>
      <IncomingSidepanel
        panelIsOpen={panelIsOpen}
        handleClose={handleClose}
        title="Incoming Transaction"
        tabs={{
          generalPath: generatePath(`${generalPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          orderPath: generatePath(`${orderPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          amlPath: generatePath(`${amlPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          logPath: generatePath(`${logPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          changeLogPath: generatePath(`${changeLogPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          warningPath: generatePath(`${warningPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          chatTab: generatePath(`${chatPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          documentsPath: generatePath(`${documentPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          commentsPath: generatePath(`${commentsPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
        }}
        isLoading={
          transactionIsLoading ||
          isLoadingAml ||
          isLoadingLog ||
          warningsIsLoading ||
          isFetching
        }
        warnings={warnings}
        document={transactionDocument}
        documentFileIsLoading={downloadInProgress || downloadIsFetching}
        downloadInProgress={downloadInProgress || downloadIsFetching}
        imgLink={imgLink}
        onDelete={onDeleteDocument}
        deleteInProgress={deleteDocumentInProgress}
        deleteIsSuccess={deleteDocumentIsSuccess}
        onMove={onMoveDocument}
        moveInProgress={documentCategoryChangeInProgress}
        onDownload={onDownloadDocument}
        sendComment={onSendDocumentComment}
        editComment={onEditComment}
        editCommentInProgress={editCommentInProgress}
        editCommentIsSuccess={editCommentIsSuccess}
        deleteComment={onDeleteComment}
        deleteCommentInProgress={deleteCommentInProgress}
        commentInProgress={commentInProgress}
        statusChangeInProgress={documentStatusChangeInProgress}
        changeCustomerDocumentsStatus={onChangeCustomerDocumentsStatus}
        onDownloadSuccess={downloadFileIsSuccess}
        getTransactionDocument={getTransactionDocumentyIdAction}
        documentIsLoading={documentIsLoading || documentIsFetching}
        activePermission={activePermissions}
        transactionInfo={transaction}
        sendMessage={sendMessage}
        currenciesList={currenciesList}
        sidepanelExpandedKey="problem-incoming"
      />
      <SidePanel
        type={SidePanelType.Transactions}
        closeOnClickOutside
        isOpen={blacklistDataVisible}
        onClose={handleCloseBlacklistData}
        title={blacklistData?.fullName as string}
        parent={
          document.getElementById('blacklist-data-sidepanel') as HTMLElement
        }
        footer={
          <ButtonPseudo text="Close" onClick={handleCloseBlacklistData} bold />
        }
        sidepanelExpandedKey="problem-incoming-blacklist"
      >
        <BlacklistDataContent data={blacklistData} />
      </SidePanel>
      <SidePanel
        type={SidePanelType.Transactions}
        closeOnClickOutside
        isOpen={blacklistItemVisibile}
        onClose={handleCloseBlacklistData}
        title="User Details"
        parent={
          document.getElementById('blacklist-data-sidepanel') as HTMLElement
        }
        footer={
          <ButtonPseudo text="Close" onClick={handleCloseBlacklistData} bold />
        }
        sidepanelExpandedKey="problem-incoming-blacklist"
        withoutExpand
        expanded
      >
        <BlacklistUserInfo info={blacklistItem} isLoading={false} />
      </SidePanel>
      <div id="blacklist-data-sidepanel" />
    </>
  );
};

export default ProblemIncomingTransactionHOC;
